import { isServiceConfirmCode } from '../utils/fulfillmentApi.mjs';

export const PICKING_STATUS = Object.freeze({
  new: { label: 'Новое', color: 'default' },
  in_progress: { label: 'В работе', color: 'primary' },
  completed: { label: 'Закрыто', color: 'success' },
  cancelled: { label: 'Отменено', color: 'error' },
});

/** Статусы, которые видит кладовщик в списке подбора */
export const ACTIVE_PICKING_STATUSES = Object.freeze(['new', 'in_progress']);

export function isActivePickingStatus(status) {
  return ACTIVE_PICKING_STATUSES.includes(status);
}

export function pickingProgress(job = {}) {
  const total = Math.max(0, Number(job.items_total) || 0);
  const picked = Math.min(total, Math.max(0, Number(job.items_picked) || 0));
  return { total, picked, percent: total ? Math.round((picked / total) * 100) : 0 };
}

export function chooseDefaultPickingJob(jobs = []) {
  return jobs.find((job) => isActivePickingStatus(job.status)) || jobs[0] || null;
}

/* ───────────── Пачечное сканирование одинаковых товаров (ТСД задания) ───────────── */

/** Повтор того же кода быстрее этого окна считаем дребезгом сканера, а не вторым товаром. */
export const SCAN_REPEAT_MS = 350;

const ARTICLE_COLLATOR = new Intl.Collator('ru', { numeric: true, sensitivity: 'base' });

function textOrEmpty(value) {
  return value == null ? '' : String(value).trim();
}

/** Артикул позиции задания для сортировки/подписи: карточка → заказ → первая линия → nmID. */
export function pickingItemArticle(item) {
  const lines = Array.isArray(item?.lines) ? item.lines : (Array.isArray(item?.products) ? item.products : []);
  return textOrEmpty(item?.product?.article)
    || textOrEmpty(item?.task?.offer_id)
    || textOrEmpty(item?.task?.article)
    || textOrEmpty(lines[0]?.article || lines[0]?.offer_id)
    || textOrEmpty(item?.task?.nm_id);
}

function pickingItemOrderId(item) {
  return textOrEmpty(item?.task?.wb_order_id ?? item?.task?.posting_number ?? item?.task?.yandex_order_id ?? item?.id);
}

/** Позиции задания по артикулу (одинаковые подряд), затем по номеру заказа; без мутации. */
export function sortPickingItemsByArticle(items) {
  return [...(items || [])].sort((x, y) => {
    const ax = pickingItemArticle(x);
    const ay = pickingItemArticle(y);
    if (!ax !== !ay) return ax ? -1 : 1;
    return ARTICLE_COLLATOR.compare(ax, ay)
      || ARTICLE_COLLATOR.compare(pickingItemOrderId(x), pickingItemOrderId(y));
  });
}

function toCount(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/**
 * Ответ `POST /picking-jobs/:id/scan/resolve` → состояние диалога пачечного скана.
 * Первый скан уже учтён: `scanned_now = 1` (если есть что собирать).
 */
export function batchFromResolve(data, scannedCode) {
  if (!data || typeof data !== 'object') return null;
  const total = toCount(data.total);
  const picked = Math.min(total, toCount(data.picked));
  const pending = data.pending == null
    ? Math.max(0, total - picked)
    : Math.min(Math.max(0, total - picked), toCount(data.pending));
  const code = textOrEmpty(scannedCode || data.scanned_barcode);
  const barcodes = new Set((Array.isArray(data.barcodes) ? data.barcodes : []).map(textOrEmpty).filter(Boolean));
  if (code) barcodes.add(code);
  const product = data.product || data.target?.product || {};
  return {
    product_key: textOrEmpty(data.product_key) || null,
    marketplace: data.marketplace || null,
    product: {
      article: textOrEmpty(product.article) || null,
      name: textOrEmpty(product.name) || null,
      image_url: product.image_url || null,
      barcode: textOrEmpty(product.barcode) || null,
      nm_id: product.nm_id ?? data.target?.task?.nm_id ?? null,
      sku: product.sku ?? null,
      offer_id: product.offer_id ?? null,
      is_b2b: Boolean(product.is_b2b),
    },
    barcodes: [...barcodes],
    total,
    picked,
    pending,
    scanned_now: pending > 0 ? 1 : 0,
    scanned_barcode: code || null,
    stock: data.stock || data.target?.stock || null,
    orders: Array.isArray(data.orders) ? data.orders : [],
    // Старый одиночный контракт WB — на случай ответа без product_key.
    confirmation_key: data.target?.confirmation_key || null,
  };
}

/** Счётчик диалога: «Товаров total/current», собрано ранее / сейчас / осталось. */
export function batchCounter(batch) {
  const total = toCount(batch?.total);
  const picked = Math.min(total, toCount(batch?.picked));
  const pending = Math.min(Math.max(0, total - picked), toCount(batch?.pending));
  const scannedNow = Math.min(pending, toCount(batch?.scanned_now));
  return {
    total,
    picked,
    pending,
    scanned_now: scannedNow,
    current: picked + scannedNow,
    remaining: Math.max(0, pending - scannedNow),
    full: pending > 0 && scannedNow >= pending,
  };
}

/** Ещё один скан того же товара в открытом диалоге: `{ batch, accepted }`. */
export function addBatchScan(batch) {
  if (!batch) return { batch, accepted: false };
  const counter = batchCounter(batch);
  if (counter.full || counter.pending === 0) return { batch, accepted: false };
  return { batch: { ...batch, scanned_now: counter.scanned_now + 1 }, accepted: true };
}

/** Код известен диалогу (ШК МП/МС этого товара) без похода на сервер. */
export function batchHasBarcode(batch, code) {
  const text = textOrEmpty(code);
  if (!text) return false;
  return (batch?.barcodes || []).includes(text);
}

/** Повтор того же кода в пределах окна — дребезг (один скан не считаем дважды). */
export function isRepeatScan(last, code, now = Date.now(), windowMs = SCAN_REPEAT_MS) {
  if (!last || textOrEmpty(last.code) !== textOrEmpty(code)) return false;
  return now - Number(last.at || 0) < windowMs;
}

/**
 * Общие «ворота» скана ТСД: во время запроса скан отбрасывается и не запоминается
 * (иначе отброшенный код блокировал бы повтор), дребезг того же кода в окне SCAN_REPEAT_MS — тоже.
 * ref — `{ current: { code, at } | null }` (useRef).
 */
export function takeScan(ref, code, { busy = false, now = Date.now() } = {}) {
  const text = textOrEmpty(code);
  if (!text || busy || !ref) return false;
  if (isRepeatScan(ref.current, text, now)) return false;
  ref.current = { code: text, at: now };
  return true;
}

/**
 * Сценарий 1 (пачка): PKR-CONFIRM = кнопка «Подтвердить (N)» при открытом диалоге,
 * без диалога — «Нет текущего товара» (на сервер не шлём). Остальное — обычный скан.
 */
export function batchScanAction(code, batchOpen) {
  if (!isServiceConfirmCode(code)) return 'scan';
  return batchOpen ? 'confirm' : 'no_current';
}
